import { z } from 'zod';

export const SHOT_TYPES = [
  'wide',
  'medium',
  'close-up',
  'birds-eye',
  'low-angle',
  'over-shoulder',
] as const;

export type ShotType = (typeof SHOT_TYPES)[number];

/**
 * Canonical page identifier format — `page-1` through `page-99`.
 * Defined once here so server and client share the same constraint
 * (round-5 H5 + round-6 R6A-H2). Phase 3 wires this into every schema that
 * carries a page id.
 */
export const PAGE_ID_REGEX = /^page-[1-9][0-9]?$/;

/**
 * Canonical Supabase storage key shape for an order's page images.
 *
 * Matches relative-to-orderId paths of the form `images/page-N.png`
 * (accepts both canonical page-N.png and legacy page_NN.png shapes).
 *
 * Producer:
 *   `uploadImageToSupabase` in apps/server/src/services/storageUploader.ts
 *   writes keys as `{orderId}/images/{image.id}.png` via string template.
 *   The producer is NOT regex-driven; it's the producer-side invariant test
 *   in storageUploader.spec.ts that asserts every produced key matches this
 *   regex, locking the upload key shape to this constant.
 *
 * Strict consumer (uses this regex):
 *   - `downloadPageImagesForRegen` in apps/server/src/agents/story-generation/productionRunner.ts
 *     — the cover- AND page-scope regen resume path that triggered issue #509.
 *     Strict because the captured filename is fed into `path.basename()` and
 *     used as the GeneratedImage.id; a permissive match would produce
 *     non-canonical ids that fail `generatePdfNode`'s page.id lookup. Legacy
 *     ids (`page_01`) are translated to canonical form (`page-1`) before
 *     being pushed into `state.generatedImages`. When BOTH shapes exist for
 *     the same page, the canonical one wins (one entry per page id).
 *
 * Permissive consumers (intentionally do NOT use this regex):
 *   - `isPageImageForIndex` (storageUploader.ts:137) — backup matching for
 *     page-scope regen; accepts legacy page_NN.png shape.
 *   - `shouldBackupFile` (storageUploader.ts:101) — same legacy reasoning.
 *   - `getFirstPageSignedUrl` filter (storageUploader.ts:607-610) — share-
 *     preview URL builder; accepts legacy shape (see audit Task 7 for the
 *     decision rationale).
 *
 * Two-sided invariant tests:
 *   - storageUploader.spec.ts (producer side: every uploaded page key matches)
 *   - startRegenerationRun.spec.ts (consumer side: filter behavior is correct)
 *
 * Together these prevent the upload/download regex pair from drifting apart
 * again. See issue #509 (regression of #374).
 *
 * Legacy data:
 *   Staging Supabase has 891 legacy `page_NN.png` files across 59 orders
 *   created between 2026-03-12 and 2026-04-16. Cleanup is disabled in
 *   staging so the data persists. The permissive regex shape above accepts
 *   both the canonical and legacy filename forms so cover regen continues
 *   to work for those orders.
 */
export const PAGE_IMAGE_KEY_RE = /^images\/page[-_]\d+\.png$/;

export const pageIdSchema = z
  .string()
  .regex(PAGE_ID_REGEX)
  .describe(
    'Stable page identifier; assigned at plan generation, immutable across regenerations.'
  );

export const storyOutlineSchema = z.object({
  title: z.string().min(5),
  bookGeneralPrompt: z
    .string()
    .min(50)
    .describe(
      'General illustration context applied to every page prompt (characters, traits, summary, moral).'
    ),
  pageDecoration: z
    .string()
    .min(10)
    .describe(
      'Decoration instructions to apply around each page illustration.'
    ),
  cover: z.object({
    imagePrompt: z
      .string()
      .min(50)
      .describe('Detailed image prompt for front cover illustration'),
    characterIds: z
      .array(z.string())
      .min(1)
      .describe('IDs of characters appearing on the front cover'),
  }),
  pages: z
    .array(
      z.object({
        id: pageIdSchema,
        imagePrompt: z.string().min(20),
        text: z.string().min(10),
        characterIds: z
          .array(z.string())
          .min(1)
          .describe(
            'IDs of characters appearing in this scene, from the charactersInformation keys'
          ),
        shotType: z.enum(SHOT_TYPES).optional(),
        setting: z.string().min(10).max(220).optional(),
      })
    )
    .min(4)
    .max(48)
    .refine((items) => new Set(items.map((p) => p.id)).size === items.length, {
      message: 'page IDs must be unique',
    }),
});

export type StoryOutline = z.infer<typeof storyOutlineSchema>;
export type StoryPage = StoryOutline['pages'][number];
